import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  ferryApi,
  type CreateFerryCredentialRequest,
  type FerryBaseStatus,
  type FerryConnectionTest,
  type FerryCredential,
} from '../client';

/** Every credentials list of the organisation, whatever carrier filter it was read with. */
function invalidateCredentials(qc: QueryClient, organisationId: string) {
  qc.invalidateQueries({ queryKey: ['ferry', 'credentials', organisationId] });
}

/** Add an account at a carrier. Stratum support's job; the secret never comes back. */
export function useCreateFerryCredential() {
  const qc = useQueryClient();
  return useMutation<FerryCredential, Error, CreateFerryCredentialRequest>({
    mutationFn: (body) => ferryApi.createCredential(body),
    onSuccess: (_c, { organisationId }) => invalidateCredentials(qc, organisationId),
  });
}

/** Enable or disable an account; a disabled one no longer enables the integration. */
export function useUpdateFerryCredentialStatus() {
  const qc = useQueryClient();
  return useMutation<
    FerryCredential,
    Error,
    { id: string; organisationId: string; status: FerryBaseStatus }
  >({
    mutationFn: ({ id, status }) => ferryApi.updateCredentialStatus(id, status),
    onSuccess: (_c, { organisationId }) => invalidateCredentials(qc, organisationId),
  });
}

export function useDeleteFerryCredential() {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: string; organisationId: string }>({
    mutationFn: ({ id }) => ferryApi.deleteCredential(id),
    onSuccess: (_c, { organisationId }) => invalidateCredentials(qc, organisationId),
  });
}

/** Ask the carrier for a token with the account; nothing is stored. */
export function useTestFerryCredential() {
  return useMutation<FerryConnectionTest, Error, string>({
    mutationFn: (id) => ferryApi.testCredential(id),
  });
}
